import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** Small uppercase kicker above the title. */
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  /** Controls rendered opposite the title (buttons, filters). */
  actions?: ReactNode;
  className?: string;
}

/**
 * The standard page header: an eyebrow, a display-font title, an optional
 * description, and an optional right-aligned actions slot. When `actions` are
 * present the header switches to a spread row layout.
 */
export function PageHeader({ eyebrow, title, description, actions, className }: PageHeaderProps) {
  return (
    <header
      className={cn("mb-6", actions && "flex flex-wrap items-end justify-between gap-4", className)}
    >
      <div>
        <p className="text-muted text-xs font-medium tracking-[0.18em] uppercase">{eyebrow}</p>
        <h2 className="font-display text-foreground mt-1 text-2xl font-normal tracking-tight sm:text-3xl">
          {title}
        </h2>
        {description && (
          <p className="text-muted mt-2 max-w-2xl text-sm leading-relaxed">{description}</p>
        )}
      </div>
      {actions}
    </header>
  );
}
